import React, {useState, useEffect } from 'react';
import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Row,
    Select,
    Table, Tooltip
} from 'antd';
import {LeftOutlined, MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';

import locale from 'antd/es/date-picker/locale/es_ES';
import dayjs from 'dayjs';
import 'dayjs/locale/es-mx';
import {isValidRfc, patternRFC} from '../../../services/utils/rfcValidator';
import {useSelector} from 'react-redux';
import {getPagePermissions, OPERATION_CERTIFICATE} from '../../../security/permissions';
import request from '../../../services/request';
dayjs.locale('es-mx');

const { TextArea } = Input;

const DynamicEquipmentList = ({ equipmentOptions, isEditable }) => {
    return (
        <Form.List name="rocOperationCertificateEquipments">
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={24} align="middle">
                            <Col span={8}>
                                <Form.Item
                                    {...restField}
                                    label="Equipo verificado"
                                    name={[name, 'roceEquipmentKey']}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor selecciona el equipo verificado.',
                                        },
                                    ]}
                                >
                                    <Select>
                                        {equipmentOptions.map((option) => (
                                            <Select.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    {...restField}
                                    label="Observaciones anteriores"
                                    name={[name, 'observations']}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor ingresa las observaciones.',
                                        },
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={2} offset={1}>
                                {fields.length > 1 ? (
                                    <Button
                                        shape="circle"
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => remove(name)}
                                    />
                                ) : null}
                            </Col>
                        </Row>
                    ))}
                    <Form.Item>
                        <Button
                            type="primary"
                            onClick={() => add()}
                            style={{width: '600px'}}
                            block
                            icon={<PlusOutlined />}
                        >
                            Agregar Equipo
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    );
};

const DynamicNormativityList = ({ isEditable }) => {
    return (
        <Form.List name="rocNormativity">
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={24} align="middle">
                            <Col span={16}>
                                <Form.Item
                                    {...restField}
                                    label="Normatividad Aplicada"
                                    name={[name, 'normativityValue']}
                                    key={key}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Por favor ingresa la normatividad Aplicada.',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Ingresa la normatividad" />
                                </Form.Item>
                            </Col>
                            <Col span={2} offset={1}>
                                {fields.length > 1 ? (
                                    <Button
                                        shape="circle"
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => remove(name)}
                                    />
                                ) : null}
                            </Col>
                        </Row>
                    ))}
                        <Form.Item>
                            <Button
                                type="dashed"
                                onClick={() => add()}
                                block
                                icon={<PlusOutlined />}
                            >
                                Agregar Normatividad
                            </Button>
                        </Form.Item>
                </>
            )}
        </Form.List>
    );
};

const CaptureTable = ({ data, onDataChange }) => {
    const handleOpinionChange = (key, checked) => {
        const newData = data.map(item =>
            item.key === key ? { ...item, opinion: checked } : item
        );
        onDataChange(newData);
    };

    const handleDetailChange = (key, e) => {
        const newData = data.map(item =>
            item.key === key ? { ...item, details: e.target.value } : item
        );
        onDataChange(newData);
    };

    const columns = [
        {
            title: 'Especificación o requerimiento',
            dataIndex: 'request',
            width: '50%',
            render: (text, record, index) => {
                if (index === 0 || index === 5) {
                    return {
                        children: text,
                        props: {
                            colSpan: 3,
                            style: {
                                backgroundColor: '#fafafa',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                whiteSpace: 'pre-line'
                            }
                        },
                    };
                }
                return text;
            },
        },
        {
            title: 'Opinión de cumplimiento',
            dataIndex: 'opinion',
            width: '15%',
            render: (opinion, record, index) => {
                if (index === 0 || index === 5) {
                    return { props: { colSpan: 0 } };
                }
                return (
                    <Checkbox
                        checked={opinion}
                        onChange={(e) => handleOpinionChange(record.key, e.target.checked)}
                    >
                        {opinion ? 'Sí cumple' : 'No cumple'}
                    </Checkbox>
                );
            },
        },
        {
            title: 'Detalle de opinión',
            dataIndex: 'details',
            render: (details, record, index) => {
                if (index === 0 || index === 5) {
                    return { props: { colSpan: 0 } };
                }
                return (
                    <TextArea
                        value={details}
                        placeholder="Detalle de opinión"
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        onChange={(e) => handleDetailChange(record.key, e)}
                    />
                );
            },
        },
    ];

    return (
        <Table
            style={{ whiteSpace: 'pre-line' }}
            bordered
            pagination={false}
            dataSource={data}
            columns={columns}
        />
    );
};

const OPTIONS_DEFAULT = [
    {value: 'UCC', label: 'UCC'},
    {value: 'Control volumétrico', label: 'Control volumétrico'},
];

export default function CertificateForm({ distributionPoint, equipments, history, location }) {

    const searchParams = new URLSearchParams(location.search);
    const certificateId = searchParams.get('certificateId');

    const isEditable = Boolean(certificateId);
    const userData = useSelector(state => state.userData);
    const permissions = userData?.permissions;
    const certificatePermissions = getPagePermissions(OPERATION_CERTIFICATE, permissions);

    const [providerRfc, setProviderRfc] = useState('');
    const [certificateIssuedDate, setCertificateIssuedDate] = useState(new Date());

    const [form] = Form.useForm();
    const [ openModalConfirm, setOpenModalConfirm] = useState(false);

    const [captureTableData, setCaptureTableData] = useState([
        { key: '0', request: 'Sistemas de medición' },
        { key: '1', request: 'Requerimientos generales del sistema de medición', opinion: false, details: '' },
        { key: '2', request: 'Requerimientos de los sistemas de medición estática', opinion: false, details: '' },
        { key: '3', request: 'Requerimientos de los sistemas de medición dinámica en ductos', opinion: false, details: '' },
        { key: '4', request: 'Requerimientos de los sistemas de medición dinámica en estaciones de servicio', opinion: false, details: '' },
        { key: '5', request: 'Programas informáticos' },
        { key: '6', request: 'Requerimientos generales', opinion: false, details: '' },
        { key: '7', request: 'Requerimientos de la información a recopilar:  \n a) Datos generales \n b) Registros del volumen \n c) Tipo de hidrocarburo o petrolífero \n d) Información fiscal', opinion: false, details: '' },
        { key: '8', request: 'Requerimientos del almacenamiento de información', opinion: false, details: '' },
        { key: '9', request: 'Requerimientos del procesamiento de la información y de la generación de reportes', opinion: false, details: '' },
        { key: '10', request: 'Requerimientos de seguridad', opinion: false, details: '' },
    ]);

    useEffect(() => {
        if (isEditable) {
            request().get(`operationCertificate/${certificateId}`)
                .then(response => {
                    const certificateData = response.data;
                    form.setFieldsValue({
                        ...certificateData,
                        rocCertificateIssuedDate: dayjs(certificateData.rocCertificateIssuedDate),
                        rocCertificateStartDate: dayjs(certificateData.rocCertificateStartDate),
                        rocCertificateEndDate: dayjs(certificateData.rocCertificateEndDate),
                    });
                    setProviderRfc(certificateData.rocProviderRfc)
                    setCertificateIssuedDate(new Date(certificateData.rocCertificateIssuedDate.replace(" ", "T")))
                    const { rocOperationCertificateDetails } = certificateData;
                    setCaptureTableData((prev) =>
                        prev.map((item) => {
                            if (item.key === '1') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdMeasuringSystemGeneralsOpinion, details: rocOperationCertificateDetails.rocdMeasuringSystemGeneralsDetail };
                            }
                            if (item.key === '2') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdMeasuringSystemStaticOpinion, details: rocOperationCertificateDetails.rocdMeasuringSystemStaticDetail };
                            }
                            if (item.key === '3') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdMeasuringSystemDynamicDuctOpinion, details: rocOperationCertificateDetails.rocdMeasuringSystemDynamicDuctDetail };
                            }
                            if (item.key === '4') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdMeasuringSystemDynamicServiceStationOpinion, details: rocOperationCertificateDetails.rocdMeasuringSystemDynamicServiceStationDetail };
                            }
                            if (item.key === '6') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdSoftwareGeneralOpinion, details: rocOperationCertificateDetails.rocdSoftwareGeneralDetail };
                            }
                            if (item.key === '7') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdSoftwareRequiredInformationOpinion, details: rocOperationCertificateDetails.rocdSoftwareRequiredInformationDetail };
                            }
                            if (item.key === '8') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdSoftwareStoredInformationOpinion, details: rocOperationCertificateDetails.rocdSoftwareStoredInformationDetail };
                            }
                            if (item.key === '9') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdSoftwareInformationProcessingOpinion, details: rocOperationCertificateDetails.rocdSoftwareInformationProcessingDetail };
                            }
                            if (item.key === '10') {
                                return { ...item, opinion: rocOperationCertificateDetails.rocdSoftwareSecurityOpinion, details: rocOperationCertificateDetails.rocdSoftwareSecurityDetail };
                            }
                            return item;
                        })
                    );
                });
        } else {
            form.resetFields();
        }
    }, [certificateId, isEditable, distributionPoint]);

    const handleBack = () => {
        history.push(location.pathname);
    };

    const onFinish = () => {
        setOpenModalConfirm(true);
    }
    const onFinishFailed = (errorInfo) => {
        notification.error({
            message: "Error en formulario",
            description: "Existen campos requeridos sin completar",
        })
    };

    const equipmentOptions = [
        ...OPTIONS_DEFAULT,
        ...equipments.map(eq => ({
                value: eq.equipmentKey,
                label: `${eq.applicationType}-${eq.equipmentKey}`
            })
        )
    ];

    const onValuesChange = (changedValues) => {
        console.log(changedValues);
        if (changedValues.rocCertificateIssuedDate !== undefined) {
            setCertificateIssuedDate(changedValues.rocCertificateIssuedDate.toDate());
        }
        if (changedValues.rocProviderRfc) {
            setProviderRfc(changedValues.rocProviderRfc
                ? changedValues.rocProviderRfc.toUpperCase()
                : '');
        }
    }


    const onConfirmData = () => {
        const formData = form.getFieldsValue(true);
        const jsonDetail = buildDetail();
        const computedFolio = `${providerRfc}${formData.rocCertificateFolio.toString().padStart(5, '0')}${certificateIssuedDate.getFullYear()}`
        const payload = {
            distributionPointId: distributionPoint.id,
            ...formData,
            rocCertificateIssuedDate: formData.rocCertificateIssuedDate.format('YYYY-MM-DD'),
            rocCertificateStartDate: formData.rocCertificateStartDate.format('YYYY-MM-DD HH:mm:ss'),
            rocCertificateEndDate: formData.rocCertificateEndDate.format('YYYY-MM-DD HH:mm:ss'),
            rocCertificateFolio: computedFolio,
            rocOperationCertificateDetails: jsonDetail,
        };
        if (isEditable) {
            request().put(`operationCertificate/${certificateId}`, payload)
                .then(response => {
                    if (response.data !== undefined) {
                        notification.success({
                            message: "El certificado se ha actualizado correctamente",
                            description: "El certificado se ha actualizado correctamente",
                        });
                        handleBack();
                    }
                });
        } else {
            request().post('operationCertificate', payload)
                .then(response => {
                    if (response.data !== undefined) {
                        notification.success({
                            message: "Se ha generado el certificado correctamente",
                            description: "Se ha generado el certificado correctamente",
                        });
                        handleBack();
                    }
                });
        }
    };
    const buildDetail = () => {
        return {
            rocdMeasuringSystemGeneralsOpinion: captureTableData[1].opinion,
            rocdMeasuringSystemGeneralsDetail: captureTableData[1].details,
            rocdMeasuringSystemStaticOpinion: captureTableData[2].opinion,
            rocdMeasuringSystemStaticDetail: captureTableData[2].details,
            rocdMeasuringSystemDynamicDuctOpinion: captureTableData[3].opinion,
            rocdMeasuringSystemDynamicDuctDetail: captureTableData[3].details,
            rocdMeasuringSystemDynamicServiceStationOpinion: captureTableData[4].opinion,
            rocdMeasuringSystemDynamicServiceStationDetail: captureTableData[4].details,
            rocdSoftwareGeneralOpinion: captureTableData[6].opinion,
            rocdSoftwareGeneralDetail: captureTableData[6].details,
            rocdSoftwareRequiredInformationOpinion: captureTableData[7].opinion,
            rocdSoftwareRequiredInformationDetail: captureTableData[7].details,
            rocdSoftwareStoredInformationOpinion: captureTableData[8].opinion,
            rocdSoftwareStoredInformationDetail: captureTableData[8].details,
            rocdSoftwareInformationProcessingOpinion: captureTableData[9].opinion,
            rocdSoftwareInformationProcessingDetail: captureTableData[9].details,
            rocdSoftwareSecurityOpinion: captureTableData[10].opinion,
            rocdSoftwareSecurityDetail: captureTableData[10].details
        }
    }

    return (
        <>
            <Form form={form} layout='vertical' onFinish={onFinish} onValuesChange={onValuesChange} onFinishFailed={onFinishFailed} style={{ paddingBottom: '100px' }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: '20px' }}>
                    <Col>
                        <span style={{ fontSize: '15px', color: '#606676' }}>
                            Llena los campos a continuación para registrar un nuevo certificado de operación.
                        </span>
                    </Col>
                    <Col>
                        <Button
                            type="default"
                            onClick={handleBack}
                            icon={<LeftOutlined />}
                            size="middle">
                                Regresar
                        </Button>
                    </Col>
                </Row>
                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Datos del proveedor que emite el certificado de verificación de la correcta operación y funcionamiento de los equipos y programas informáticos
                    </span>
                </Divider>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Punto de Distribución"
                            name="puntoDistribucion"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                            help="Punto de Distribución seleccionado"
                            style={{ 'fontSize': 'small' }}
                            initialValue={distributionPoint.name}
                        >
                            <Input placeholder="Ingresa el punto de distribución" disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Nombre o razón social"
                            name="rocProviderBusinessName"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa el nombre o razón social" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="RFC"
                            name="rocProviderRfc"
                            rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
                            normalize={value => value.toUpperCase()}
                        >
                            <Input placeholder="Ingresa el RFC" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Domicilio en el que se prestó el servicio de verificación
                    </span>
                </Divider>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Calle"
                            name="rocStreet"
                            initialValue={distributionPoint.street}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa la calle" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Número"
                            name="rocNumber"
                            initialValue={distributionPoint.number}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa el número" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Colonia"
                            name="rocColony"
                            initialValue={distributionPoint.colony}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa la colonia" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Código postal"
                            name="rocPostalCode"
                            initialValue={distributionPoint.postalCode}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa el código postal" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Estado"
                            name="rocState"
                            initialValue={distributionPoint.state}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa el estado" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Municipio"
                            name="rocMunicipality"
                            initialValue={distributionPoint.municipality}
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa el municipio" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                    Información del certificado emitido
                    </span>
                </Divider>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                        <Form.Item
                            label="Folio"
                            name="rocCertificateFolio"
                            rules={[
                                { required: true, message: 'Este campo es obligatorio' }
                            ]}
                            tooltip='Requerido para expresar el número de folio del certificado. Deberá conformarse de los doce o trece caracteres del RFC del proveedor, seguido de cinco números únicos y consecutivos que correspondan al orden cronológico de emisión del certificado en el año, seguidos de cuatro números que correspondan al año de emisión del mismo.'
                        >
                            {isEditable ?
                                <Input disabled />
                                :
                                <InputNumber min={0} max={99999} addonBefore={providerRfc || ''} style={{ width: '100%' }} addonAfter={certificateIssuedDate.getFullYear()} />
                            }

                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Form.Item
                            label="Fecha emisión Certificado"
                            name="rocCertificateIssuedDate"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <DatePicker locale={locale} style={{ width: '100%' }}  />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Form.Item
                            label="Inicio de Verificación"
                            name="rocCertificateStartDate"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <DatePicker locale={locale} style={{ width: '100%' }}  />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Form.Item
                            label="Fin de verificación"
                            name="rocCertificateEndDate"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <DatePicker locale={locale} style={{ width: '100%' }}  />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left" style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}>
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Normatividad Aplicada
                    </span>
                </Divider>

                <DynamicNormativityList isEditable={isEditable} />

                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Equipos y programas verificados
                    </span>
                </Divider>

                <DynamicEquipmentList equipmentOptions={equipmentOptions} isEditable={isEditable} />

                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                    Tabla de cumplimiento de los requisitos verificado
                    </span>
                </Divider>
                <CaptureTable data={captureTableData} onDataChange={setCaptureTableData} />
                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                      Resultado de la verificación
                    </span>
                </Divider>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <Form.Item
                            label="Resultado de la verificación"
                            name="rocCertificateResult"
                            valuePropName="checked"
                        >
                            <Checkbox>Acreditado</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Información del personal del proveedor autorizado que emite el certificado
                    </span>
                </Divider>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="RFC de personal del proveedor"
                            name="rocPersonalProviderRfc"
                            rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
                            normalize={value => value.toUpperCase()}
                        >
                            <Input placeholder="Ingresa el RFC del personal" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Nombre del personal del proveedor"
                            name="rocPersonalProviderName"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa los nombres y apellidos" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider
                    orientation="left"
                    style={{ margin: '10px 0', fontSize: '15px', color: '#606676' }}
                >
                    <span style={{ display: 'inline-block', maxWidth: '300px', width: '100%' }}>
                        Información del representante legal del proveedor autorizado por el SAT
                    </span>
                </Divider>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="RFC de representante legal del proveedor"
                            name="rocLegalRepresentativeRfc"
                            rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
                            normalize={value => value.toUpperCase()}
                        >
                            <Input placeholder="Ingresa el RFC del personal" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Nombre del representante legal"
                            name="rocLegalRepresentativeName"
                            rules={[{ required: true, message: 'Este campo es obligatorio' }]}
                        >
                            <Input placeholder="Ingresa los nombres y apellidos" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24} style={{ textAlign: 'right' }}>
                        <Button type="primary" size="middle" htmlType="submit">
                            {isEditable ? 'Editar Certificado' : 'Agregar certificado'}
                        </Button>
                    </Col>
                </Row>
                <Modal
                    open={openModalConfirm}
                    title="Atención"
                    footer={[
                        <Button className="back-button" key="back" onClick={() => setOpenModalConfirm(false)}>Revisar información</Button>,
                        <Button className="submit-button" key="submit" onClick={onConfirmData}>Confirmar</Button>
                    ]}
                >
                    <p>Por favor asegúrese de que la información en el formulario sea la correcta.</p>
                </Modal>
            </Form>
        </>
    );
}